/**
 * The frame: brand, top bar, sidebar, main. Every screen of tasks 42–48, 53 and 57
 * renders inside it.
 *
 * Two accessibility behaviours live here rather than in each screen, because nine
 * screens getting them individually right is nine chances to get them wrong:
 *
 *  - **A skip link**, first in the tab order, jumping past sixteen menu items to the
 *    content. Without it a keyboard user pays for the whole sidebar on every
 *    navigation.
 *  - **Focus moves into the new screen after a route change** — to its `<h1>` if it
 *    has one, otherwise to `<main>`. Not on the first render: focus on load belongs
 *    where the browser put it. An SPA that never moves focus leaves a screen-reader
 *    user at the top of the sidebar while the page under them has changed, with no
 *    announcement that anything happened.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router';

import { useT } from '../i18n';
import type { SessionUser } from '../api/types';
import { ErrorBoundary } from '../states/ErrorBoundary';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

export interface AppShellProps {
  readonly user: SessionUser;
  readonly onSignOut: () => void;
  /**
   * What goes in the top bar's timer slot. Task 41 left the slot named and empty;
   * Task 42 fills it with `<MiniTimer />`, which reads the one `TimerProvider`
   * mounted above this component in `App.tsx`. It is a prop rather than an import
   * so the shell stays renderable without a timer — the login, company-blocked and
   * select-company trees have no `TimerProvider` around them, and a shell that
   * imported `useTimer()` directly would throw in all three.
   */
  readonly timerSlot?: ReactNode;
  readonly children: ReactNode;
}

export function AppShell({ user, onSignOut, timerSlot, children }: AppShellProps): ReactNode {
  const t = useT();
  const location = useLocation();
  const mainRef = useRef<HTMLElement | null>(null);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const main = mainRef.current;
    if (main === null) return;
    const heading = main.querySelector('h1');
    (heading ?? main).focus();
  }, [location.pathname]);

  return (
    <div className="wp-shell">
      <a className="wp-skip-link" href="#main">
        {t('shell.skipToContent')}
      </a>

      {/* The brand block is the top of the desk edge, not a fourth region of the
          grid: DESIGN.md §1.1 wants one continuous black surface from the top of
          the window down, and a brand cell of its own would put a seam across it. */}
      <div className="wp-shell__rail">
        <div className="wp-shell__brand">{t('app.name')}</div>
        <Sidebar permissions={user.permissions} />
      </div>

      <TopBar
        user={user}
        onSignOut={onSignOut}
        {...(timerSlot === undefined ? {} : { timerSlot })}
      />

      <main
        id="main"
        className="wp-shell__main"
        ref={mainRef}
        tabIndex={-1}
        aria-label={t('shell.mainLandmark')}
      >
        {/* The tenth state, and it lives here rather than in each screen for the
            same reason the skip link does: a render error caught above `main`
            would take the rail and the top bar with it, and one caught inside a
            screen is thirty-eight chances to forget. Keyed by the path so
            navigating away from a broken screen clears the note by unmounting the
            boundary — nobody should have to press "try again" to leave. */}
        <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
      </main>
    </div>
  );
}
